import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { setupMobX } from '../util/setupMobX';
import { getStateOfBaselineSurveyData } from '../request/authenticated-requests/questionnaire';
import {
  AppointmentType,
  Bdm1Outcome,
  CohortType,
  NachversandKind,
  RetrievalMode,
  StudyGroup,
} from '@prisma/client';
import { DateTime } from 'luxon';
import { showErrorNotification } from '../component/notification';
import { MyCohortStore } from './MyCohortStore';
import type {
  NachversandAppointmentResDto,
  NachversandAppointmentType,
  ParticipantBdmStateResDto,
  UserBmsSampleResDto,
} from '@web/common';
import {
  BDM1_INVITATION_DAY_OFFSET,
  BDM1_SAMPLE_BOOKING_DEADLINE_DAY_OFFSET,
  BDM2_SAMPLE_BOOKING_DEADLINE_DAY_OFFSET,
  BDM2_SURVEY_DAY_OFFSET,
  ErrorCode,
  RETRIEVAL_MODE_BY_BDM2_VARIANT,
  bdmDayStarted,
  sampleDeadline as resolveSampleDeadline,
  bdmDeadline,
  bdmDeadlinePassed,
} from '@web/common';
import {
  getMyBmsSample,
  getMyBdmState,
} from '../request/authenticated-requests/participant';
import {
  getNachversandBookingLink,
  getNachversandAppointment,
} from '../request/authenticated-requests/appointment';
import { handleRequestError } from '../request/handleRequestError';
// stepsData must never import from this store (import cycle) — it only shares
// the StepKey type and the visibility predicates in this direction.
import {
  isBdm1StepVisible,
  isBdm2StepVisible,
  isStep3Visible,
} from '../view/authenticated/participant/waiting-phase/stepsData';
import { StepKey } from '../view/authenticated/participant/waiting-phase/stepTypes';
import { pickActiveNachversand } from '../view/authenticated/participant/waiting-phase/nachversandBannerState';
import type { ActiveNachversand } from '../view/authenticated/participant/waiting-phase/nachversandBannerState';

// TODO: https://quickbird.atlassian.net/browse/SCC-697
export class WaitingStateStore {
  @observable public baselineSurveyFilledOut: boolean = false;
  @observable public loadingBaselineState: boolean = false;
  @observable public baseLineSurveyLink: string | undefined = undefined;
  // the bdm-state endpoint response as-is: studyGroup, the due BDM surveys, the BDM1
  // outcome + voucher status and bdmStartDate (BDM day 0); null = not loaded
  @observable public bdmState: ParticipantBdmStateResDto | null = null;
  // Nachversand booking link for the SAMPLE_MAIL outcome; null while not loaded
  // or when the fetch failed — the step text then drops the link paragraph.
  @observable public bdm1BookingLink: string | null = null;
  // the participant's newest booked BDM1 Nachversand video call, past ones included;
  // how long a finished one still counts is decided in isAppointmentStillCurrent
  @observable
  public bdm1Appointment: NachversandAppointmentResDto['appointment'] = null;
  // true once the BDM1 sample is dispatched, returned, picked up or was a voucher — then the
  // banner has nothing left to offer. false while not loaded or when the fetch failed.
  @observable public bdm1RetrievalSettled: boolean = false;
  // postal or pickup fulfilment of the BDM1 sample; a revoke flips MAIL to PICKUP, which
  // decides the step paragraph and the deadline. null while not loaded or without a row.
  @observable public bdm1RetrievalMode: RetrievalMode | null = null;

  // the same four for the BDM2 4g sample. The imported outcome names the fulfilment in its
  // variant (mail*/pickup*/noProduct), so the retrieval mode below is only the fallback while
  // no outcome has arrived — bdm2EffectiveRetrievalMode holds that rule.
  @observable public bdm2BookingLink: string | null = null;
  @observable
  public bdm2Appointment: NachversandAppointmentResDto['appointment'] = null;
  @observable public bdm2RetrievalSettled: boolean = false;
  @observable public bdm2RetrievalMode: RetrievalMode | null = null;

  @observable public bmsSample: UserBmsSampleResDto | null = null;

  // Baseline first, then most-advanced-state-first; derives purely from
  // observables, so the order in which responses arrive cannot matter.
  // Baseline first, afterwards the most advanced reached state wins (step-4 down
  // to step-1); the hold-back/loading rationale is documented in waiting-phase/README.md.
  @computed public get currentStepKey(): StepKey {
    if (!this.baselineSurveyFilledOut) {
      return StepKey.Step0;
    }
    const studyGroup = this.bdmState?.studyGroup ?? null;
    const hasOpenGroup1Survey =
      studyGroup === StudyGroup.GROUP_1 &&
      [this.bdmState?.bdm1Survey, this.bdmState?.bdm2Survey].some(
        (survey) => survey != null && survey.filledOutAt === null,
      );
    // step-4 ("Studienstart") waits until the bdm-state is loaded and no due survey is open anymore
    if (
      this.bdmState !== null &&
      this.myCohortType !== CohortType.NONE &&
      !hasOpenGroup1Survey
    ) {
      return StepKey.Step4;
    }
    // step-3 ("Zuteilung Studiengruppe") = randomization day reached AND no cohort yet
    // AND no open tasks (GROUP_1: no open survey and the BDM2 sample settled; GROUP_2
    // never has tasks). A cohort arriving first simply skips it.
    if (
      isStep3Visible(
        studyGroup,
        this.myCohortStore.cohortType,
        hasOpenGroup1Survey,
        this.bdmState?.bdmStartDate ?? null,
        this.bdm2RetrievalSettled,
      )
    ) {
      return StepKey.Step3;
    }
    if (
      isBdm2StepVisible(
        studyGroup,
        this.isBdm2SurveyDue,
        this.bdm2SurveyFilledOut,
        this.bdm2Outcome,
      )
    ) {
      return StepKey.StepBdm2;
    }
    // the fillout alone advances — the outcome may lag behind the SCC-815 cron
    if (
      isBdm1StepVisible(
        studyGroup,
        this.bdm1SurveyFilledOut,
        this.bdmState?.bdm1Outcome ?? null,
      )
    ) {
      return StepKey.StepBdm1;
    }
    // Step 2 is the call to fill out survey 1, and that invitation only goes out
    // at the cohort's BDM day 0. Before that there is nothing to do — regardless
    // of whether the group has already been assigned — so the step stays a
    // preview in the stepper instead of becoming the current task.
    if (studyGroup !== null && this.isBdmStarted) {
      return StepKey.Step2;
    }
    return StepKey.Step1;
  }

  /** Whether the cohort's BDM day 0 has been reached (Europe/Zurich). */
  @computed public get isBdmStarted(): boolean {
    return bdmDayStarted(
      this.bdmState?.bdmStartDate ?? null,
      BDM1_INVITATION_DAY_OFFSET,
    );
  }

  /**
   * Whether the BDM2 survey day offset (day 28) has been reached (Europe/Zurich).
   * The anchor is a cohort date and reaches every study group, so callers that mean
   * GROUP_1 must check the group themselves.
   */
  @computed public get isBdm2Started(): boolean {
    return bdmDayStarted(
      this.bdmState?.bdmStartDate ?? null,
      BDM2_SURVEY_DAY_OFFSET,
    );
  }

  // the imported BDM2 draw (product, price, payout); null until the ETH outcome file
  // was imported, and always null outside GROUP_1
  @computed public get bdm2Outcome(): ParticipantBdmStateResDto['bdm2Outcome'] {
    return this.bdmState?.bdm2Outcome ?? null;
  }

  @computed public get bdm1SurveyFilledOut(): boolean {
    return (this.bdmState?.bdm1Survey?.filledOutAt ?? null) !== null;
  }

  @computed public get bdm2SurveyFilledOut(): boolean {
    return (this.bdmState?.bdm2Survey?.filledOutAt ?? null) !== null;
  }

  // BDM1 opens at the cohort anchor itself (day 0) — an early link import must
  // not surface the survey before the cohort's BDM start.
  @computed public get isBdm1SurveyDue(): boolean {
    const state = this.bdmState;
    // data prerequisites: response loaded, link imported, cohort anchor known
    if (
      state === null ||
      state.bdm1Survey === null ||
      state.bdmStartDate === null
    ) {
      return false;
    }
    const isGroup1 = state.studyGroup === StudyGroup.GROUP_1;
    const offsetReached = bdmDayStarted(
      state.bdmStartDate,
      BDM1_INVITATION_DAY_OFFSET,
    );
    return isGroup1 && !this.bdm1SurveyFilledOut && offsetReached;
  }

  // BDM2 opens at the fixed study offset (day 28 from the cohort anchor) and only
  // after survey 1 is filled out — an early link import must not surface it sooner.
  @computed public get isBdm2SurveyDue(): boolean {
    const state = this.bdmState;
    // data prerequisites: response loaded, link imported, cohort anchor known
    if (
      state === null ||
      state.bdm2Survey === null ||
      state.bdmStartDate === null
    ) {
      return false;
    }
    const isGroup1 = state.studyGroup === StudyGroup.GROUP_1;
    const offsetReached = bdmDayStarted(
      state.bdmStartDate,
      BDM2_SURVEY_DAY_OFFSET,
    );
    return (
      isGroup1 &&
      this.bdm1SurveyFilledOut &&
      !this.bdm2SurveyFilledOut &&
      offsetReached
    );
  }

  // whether a Nachversand may still be booked; staff can revoke it after the outcome was
  // written. Stays true while the bdm-state is not loaded — a pending or failed fetch must
  // not hide a legitimate banner. The webhook fails closed, so the UI can fail open.
  @computed public get nachversandEligible(): boolean {
    return this.bdmState?.nachversandEligible ?? true;
  }

  // The sample can be handed in until the study starts — the same window the reminder
  // cron uses, so the banner never disappears while the mails still ask for it. Without a
  // known start date the banner stays visible.
  @computed public get isBmsPending(): boolean {
    const undecided =
      this.bmsSample !== null &&
      this.bmsSample.broughtAt === null &&
      this.bmsSample.decidedNotToBringAt === null;
    if (!undecided || this.myCohortStartDate === null) {
      return undecided;
    }
    return DateTime.now() < this.myCohortStartDate;
  }

  // The participant's own 1g sample deadline: mail -> video-call booking, pickup -> shop
  // pickup, null when none applies. The retrieval mode wins over the outcome, which a
  // revoked eligibility leaves at SAMPLE_MAIL.
  @computed public get bdm1SampleDeadline(): DateTime | null {
    if (this.bdmState === null) {
      return null;
    }
    return resolveSampleDeadline(
      NachversandKind.BDM1,
      this.bdmState.bdmStartDate,
      this.bdmState.bdm1Outcome,
      this.bdm1RetrievalMode,
    );
  }

  // gates the Nachversand banner: only a SAMPLE_MAIL outcome can book, and only until the BOOKING
  // deadline. Deliberately not bdm1SampleDeadline — that one follows the retrieval mode, so a
  // revoked participant (mode PICKUP, outcome still SAMPLE_MAIL) would stretch this window to the
  // four-days-later pickup deadline.
  @computed public get isBdm1BookingWindowOpen(): boolean {
    const bdmStartDate = this.bdmState?.bdmStartDate ?? null;
    if (bdmStartDate === null) {
      return false;
    }
    const deadline = bdmDeadline(
      bdmStartDate,
      BDM1_SAMPLE_BOOKING_DEADLINE_DAY_OFFSET,
    );
    return (
      this.bdmState?.bdm1Outcome === Bdm1Outcome.SAMPLE_MAIL &&
      !bdmDeadlinePassed(deadline)
    );
  }

  // The fulfilment mode of the BDM2 4g sample, for everything that has to agree on it. An
  // imported outcome already carries the mode it was derived from, so it beats the retrieval
  // mode from the separate nachversand request: that one can still be in flight or have failed.
  @computed private get bdm2EffectiveRetrievalMode(): RetrievalMode | null {
    const outcome = this.bdmState?.bdm2Outcome ?? null;
    if (outcome === null) {
      return this.bdm2RetrievalMode;
    }
    return RETRIEVAL_MODE_BY_BDM2_VARIANT[outcome.variant];
  }

  // The participant's own 4g sample deadline: mail -> video-call booking, pickup -> shop
  // pickup, null when none applies.
  @computed public get bdm2SampleDeadline(): DateTime | null {
    if (this.bdmState === null) {
      return null;
    }
    return resolveSampleDeadline(
      NachversandKind.BDM2,
      this.bdmState.bdmStartDate,
      null,
      this.bdm2EffectiveRetrievalMode,
    );
  }

  // gates the BDM2 Nachversand banner. The MAIL check does the work bdm1Outcome ===
  // SAMPLE_MAIL does for BDM1: READY_FOR_PICKUP is not a settled status, so without it a
  // pickup participant would be offered a video call.
  @computed public get isBdm2BookingWindowOpen(): boolean {
    const bdmStartDate = this.bdmState?.bdmStartDate ?? null;
    if (bdmStartDate === null) {
      return false;
    }
    const deadline = bdmDeadline(
      bdmStartDate,
      BDM2_SAMPLE_BOOKING_DEADLINE_DAY_OFFSET,
    );
    return (
      this.bdm2EffectiveRetrievalMode === RetrievalMode.MAIL &&
      !bdmDeadlinePassed(deadline)
    );
  }

  // Bundled per kind so MobX caches each side on its own and the choice stays pure policy.
  // Both are always built; pickActiveNachversand drops what the banner would not render.
  @computed private get bdm1Nachversand(): ActiveNachversand {
    return {
      kind: AppointmentType.NACHVERSAND_BDM1,
      bookingLink: this.bdm1BookingLink,
      appointment: this.bdm1Appointment,
      settled: this.bdm1RetrievalSettled,
      sampleDeadline: this.bdm1SampleDeadline,
      bookingWindowOpen: this.isBdm1BookingWindowOpen,
    };
  }

  @computed private get bdm2Nachversand(): ActiveNachversand {
    return {
      kind: AppointmentType.NACHVERSAND_BDM2,
      bookingLink: this.bdm2BookingLink,
      appointment: this.bdm2Appointment,
      settled: this.bdm2RetrievalSettled,
      sampleDeadline: this.bdm2SampleDeadline,
      bookingWindowOpen: this.isBdm2BookingWindowOpen,
    };
  }

  @computed public get activeNachversand(): ActiveNachversand | null {
    return pickActiveNachversand(this.bdm1Nachversand, this.bdm2Nachversand);
  }

  @computed public get myCohortStartDate(): null | DateTime {
    return this.myCohortStore.cohortStartDate;
  }
  @computed public get myCohortOnboardingEndDate(): null | DateTime {
    return this.myCohortStore.cohortOnboardingEndDate;
  }
  @computed public get myCohortType() {
    return this.myCohortStore.cohortType ?? CohortType.NONE;
  }
  @computed public get canAccessStudy(): boolean {
    return (
      this.myCohortType !== CohortType.NONE &&
      !!this.myCohortStartDate &&
      this.myCohortStartDate <= DateTime.now()
    );
  }
  @computed public get loadingAccessState(): boolean {
    return this.myCohortStore.loading;
  }

  constructor(private readonly myCohortStore: MyCohortStore) {
    makeObservable(this);
  }

  @action public loadDataAndCheckAccess() {
    // first checking if baseline survey is filled out
    this.loadBaselineSurveyData();
    // if so, it will check the cohort type
    // after that it will check the start date
    this.loadBmsSample();
  }

  @action public loadBdmState() {
    getMyBdmState()
      .then((response) =>
        runInAction(() => {
          this.bdmState = response;
          // Only GROUP_1 ever gets a sample, for either part.
          if (response.studyGroup !== StudyGroup.GROUP_1) {
            return;
          }
          if (response.bdm1Outcome === Bdm1Outcome.SAMPLE_MAIL) {
            this.loadNachversandBookingLink(AppointmentType.NACHVERSAND_BDM1);
            this.loadNachversandAppointment(AppointmentType.NACHVERSAND_BDM1);
          }
          // An imported outcome counts next to the day-28 offset: the import has no gate
          // of its own, and the fill-out flag lags behind its cron while the reminder
          // mails already ask for the booking.
          if (this.isBdm2Started || response.bdm2Outcome !== null) {
            this.loadNachversandBookingLink(AppointmentType.NACHVERSAND_BDM2);
            this.loadNachversandAppointment(AppointmentType.NACHVERSAND_BDM2);
          }
        }),
      )
      .catch((error) =>
        handleRequestError(error, [
          ErrorCode.RecordNotFound,
          () => runInAction(() => (this.bdmState = null)),
        ]),
      );
  }

  // failure is deliberately silent — the banner disables its button instead
  @action private loadNachversandBookingLink(type: NachversandAppointmentType) {
    const isBdm1 = type === AppointmentType.NACHVERSAND_BDM1;
    // the link is stable per participant — once loaded, a bdm-state refresh
    // (e.g. after a voucher redeem) must not refetch it
    if ((isBdm1 ? this.bdm1BookingLink : this.bdm2BookingLink) !== null) {
      return;
    }
    const assign = (url: string | null) =>
      runInAction(() => {
        if (isBdm1) {
          this.bdm1BookingLink = url;
        } else {
          this.bdm2BookingLink = url;
        }
      });
    getNachversandBookingLink(type)
      .then((response) => assign(response.url))
      .catch(() => assign(null));
  }

  @action private loadNachversandAppointment(type: NachversandAppointmentType) {
    const isBdm1 = type === AppointmentType.NACHVERSAND_BDM1;
    const assign = (state: NachversandAppointmentResDto) =>
      runInAction(() => {
        if (isBdm1) {
          this.bdm1Appointment = state.appointment;
          this.bdm1RetrievalSettled = state.retrievalSettled;
          this.bdm1RetrievalMode = state.retrievalMode;
        } else {
          this.bdm2Appointment = state.appointment;
          this.bdm2RetrievalSettled = state.retrievalSettled;
          this.bdm2RetrievalMode = state.retrievalMode;
        }
      });
    getNachversandAppointment(type)
      .then((response) => assign(response))
      .catch(() =>
        assign({
          appointment: null,
          retrievalSettled: false,
          retrievalMode: null,
        }),
      );
  }

  @action private loadBmsSample() {
    getMyBmsSample()
      .then((response) => runInAction(() => (this.bmsSample = response)))
      .catch((error) =>
        handleRequestError(error, [
          ErrorCode.RecordNotFound,
          () => runInAction(() => (this.bmsSample = null)),
        ]),
      );
  }

  @action private loadBaselineSurveyData() {
    this.loadingBaselineState = true;
    getStateOfBaselineSurveyData()
      .then((res) => {
        runInAction(() => {
          if (res.state === 'notFilledOutYet') {
            this.baseLineSurveyLink = res?.link ?? undefined;
          } else if (res.state === 'alreadyFilledOut') {
            this.baselineSurveyFilledOut = true;
            this.loadBdmState();
          }
        });
      })
      .catch(() => {
        showErrorNotification();
      })
      .finally(() => {
        runInAction(() => (this.loadingBaselineState = false));
      });
  }
}

const { provider, useStore } = setupMobX<WaitingStateStore>();
export const useWaitingStateStore = useStore;
export const WaitingStateStoreProvider = provider;
